import {
  TimesheetDepartment,
  TimesheetDepartmentGroup,
  type TimesheetDepartmentList,
} from '@kalos/kalos-rpc';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { TimesheetDepartmentClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type TimesheetDepartmentFilters = EntityFilter<TimesheetDepartment>;

export const useTimesheetDepartmentListQuery = <TSelectData = TimesheetDepartmentList,>({
  enabled = true,
  filter = {},
  select,
  options,
}: {
  enabled?: boolean;
  filter?: TimesheetDepartmentFilters;
  select?: (
    data: Awaited<ReturnType<typeof TimesheetDepartmentClientService.BatchGet>>,
  ) => TSelectData;
  options?: Partial<UseQueryOptions<TimesheetDepartmentList, Error, TSelectData>>;
} = {}) => {
  return useQuery({
    queryKey: [
      queryKeys.timesheetDepartments.root,
      queryKeys.timesheetDepartments.list,
      queryKeys.timesheetDepartments.getFiltersHash(filter),
    ],
    queryFn: async () => {
      const departmentRequest = TimesheetDepartment.create(filter);
      return TimesheetDepartmentClientService.BatchGet(departmentRequest);
    },
    enabled,
    select,
    ...options,
  });
};

export const useTimesheetDepartmentQuery = <TSelectData = TimesheetDepartment,>({
  enabled = true,
  filter = {},
  select,
}: {
  enabled?: boolean;
  filter?: TimesheetDepartmentFilters;
  select?: (data: Awaited<ReturnType<typeof TimesheetDepartmentClientService.Get>>) => TSelectData;
} = {}) => {
  return useQuery({
    queryKey: [
      queryKeys.timesheetDepartment.root,
      queryKeys.timesheetDepartment.getFiltersHash(filter),
    ],
    queryFn: async () => {
      const departmentRequest = TimesheetDepartment.create(filter);
      return TimesheetDepartmentClientService.Get(departmentRequest);
    },
    enabled,
    select,
  });
};

/** Department group names (the sector each names), by group id. Few and fixed, read once. */
export const useTimesheetDepartmentGroupNamesQuery = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: [queryKeys.timesheetDepartmentGroups.root],
    queryFn: () =>
      TimesheetDepartmentClientService.BatchGetGroup(TimesheetDepartmentGroup.create()),
    select: (data) => new Map(data.results.map((group) => [group.id, group.sector.trim()])),
    staleTime: Infinity,
    enabled,
  });
